import type { FeToolParameter, FeToolSchema } from "@/services/agent";
import type { OsAniContextSnapshot } from "@/components/ui/os-layout/buildOsAniContextSnapshot";
import type {
  FeToolPickerArtifactItem,
  FeToolPickerReviewTaskItem,
} from "@/services/api/chatDebugMenu";

export type FeToolDebugPickerOptions = {
  artifacts: FeToolPickerArtifactItem[];
  reviewTasks: FeToolPickerReviewTaskItem[];
};

export type ParamFieldKind = "default" | "ref_picker" | "json_object";

export type RefSuggestion = { ref: string; label: string };

export function resolveParamFieldKind(param: FeToolParameter): ParamFieldKind {
  if (param.name === "ref") {
    return "ref_picker";
  }
  if (param.type === "object" || param.type === "array") {
    return "json_object";
  }
  return "default";
}

/** Ref suggestions for the developer form: open windows, focused-window nav targets, outputs and reviews. */
export function buildRefSuggestions(
  osContext: OsAniContextSnapshot | undefined,
  pickerOptions: FeToolDebugPickerOptions,
  feOpenAppAllowlist: readonly string[] | undefined,
): RefSuggestion[] {
  const windows = osContext?.workspaces.find((ws) => ws.isActive)?.windows ?? [];
  const suggestions: RefSuggestion[] = [
    ...windows.map((w) => ({ ref: w.ref, label: w.title })),
    ...(osContext?.focusedWindow?.nav_targets ?? []).map((target) => ({ ref: target.ref, label: `${target.label} (${target.kind})` })),
    ...(feOpenAppAllowlist ?? []).map((appId) => ({ ref: `app:${appId}`, label: appId })),
    ...pickerOptions.artifacts.map((item) => ({ ref: `artifact:${item.id}`, label: item.name })),
    ...pickerOptions.reviewTasks.map((item) => ({ ref: `review:${item.task_id}`, label: item.title || item.client_name || item.task_id })),
  ];
  const seen = new Set<string>();
  return suggestions.filter((item) => !seen.has(item.ref) && seen.add(item.ref));
}

export function toolHasNoParams(schema: FeToolSchema): boolean {
  return schema.parameters.length === 0;
}
